import React, { useState, useEffect, useMemo } from "react";
import styled from "styled-components";
import { Box, Button, Spacer, Notify, Text } from "@flexisaf/flexibull2";
import { Modal, Select } from "@/features/ui";
import { Icon } from "@/features/ui/base/icon";
import { usePrayerController } from "./use-prayer";
import { useGetUpcomingMeetingsForPrayersQuery } from "@/features/meetings/meeting.api";
import { Unit } from "@/utils/enums";
import { useAuth } from "@/features/auth";
import theme from "@/style/theme";

interface DownloadAttachmentsModalProps {
    isOpen: boolean;
    onClose: () => void;
}

const FilterLabelWrapper = styled.div`
    display: flex;
    align-items: center;
`;

const IconWrapper = styled.div`
    margin-right: 5px;
`;

export const DownloadAttachmentsModal: React.FC<DownloadAttachmentsModalProps> = ({
    isOpen,
    onClose,
}) => {
    const { user } = useAuth();
    const [selectedFacultyId, setSelectedFacultyId] = useState<string>("");
    const [selectedDepartmentId, setSelectedDepartmentId] = useState<string>("");
    const [selectedMeetingId, setSelectedMeetingId] = useState<string>("");

    // Determine which faculty ID to use for fetching departments
    const facultyIdForDepartments = useMemo(() => {
        if (!user) return selectedFacultyId;

        const userUnit = user.currentRole?.unit;

        // For Faculty users, use their facultyId if no faculty is selected
        if (userUnit === Unit.Faculty && !selectedFacultyId && user.faculty?.facultyId) {
            return user.faculty.facultyId;
        }

        return selectedFacultyId;
    }, [user, selectedFacultyId]);

    // Get faculty and department options
    const {
        userDetails: initialUserDetails,
        facultyOptions,
        // departmentOptions,
        loading: {
            isLoadingFaculties,
            isLoadingDepartments,
            isDownloadingAttachmentsByMeetingAndDepartment,
        },
        downloadAttachmentsByMeetingAndDepartment,
    } = usePrayerController(facultyIdForDepartments);

    // Get department options when faculty changes
    const { departmentOptionsById: filteredDepartmentOptions } = usePrayerController(facultyIdForDepartments);
    // Get upcoming meetings for prayers (already transformed by RTK)
    const { data: meetingOptions = [], isLoading: isLoadingMeetings } =
        useGetUpcomingMeetingsForPrayersQuery();

    // Set default values based on current user's role
    useEffect(() => {
        if (!user || !isOpen) return;

        const userUnit = user.currentRole?.unit;

        // If user is not Senate or SBC, use their department and faculty as defaults
        if (userUnit !== Unit.Senate && userUnit !== Unit.Sbc) {
            if (user.faculty?.facultyId) {
                setSelectedFacultyId(user.faculty.facultyId);
            }
            if (user.department?.id) {
                setSelectedDepartmentId(user.department.id);
            }
        }
    }, [user, isOpen]);

    const handleFacultyChange = (facultyId: string) => {
        setSelectedFacultyId(facultyId);
        setSelectedDepartmentId(""); // Reset department when faculty changes
    };

    const handleDepartmentChange = (departmentId: string) => {
        setSelectedDepartmentId(departmentId);
    };

    const handleMeetingChange = (meetingId: string) => {
        setSelectedMeetingId(meetingId);
    };

    const handleDownloadAttachments = async () => {
        if (!selectedMeetingId || !selectedDepartmentId) {
            return;
        }

        try {
            await downloadAttachmentsByMeetingAndDepartment(
                parseInt(selectedMeetingId),
                selectedDepartmentId ?? "" // Use empty string if department not required
            );
        } catch (error) {
            Notify("Failed to download attachments", { status: "error" });
        }
    };

    const resetForm = () => {
        const userUnit = user?.currentRole?.unit;

        // If user is not Senate or SBC, reset to their department and faculty
        if (userUnit !== Unit.Senate && userUnit !== Unit.Sbc) {
            setSelectedFacultyId(user?.faculty?.facultyId ?? "");
            setSelectedDepartmentId(user?.department?.departmentId ?? "");
        } else {
            setSelectedFacultyId("");
            setSelectedDepartmentId("");
        }
        setSelectedMeetingId("");
    };

    const handleClose = () => {
        resetForm();
        onClose();
    };

    // Determine what fields are visible based on user unit
    const isFacultyVisible =
        initialUserDetails.unit === Unit.Sbc || initialUserDetails.unit === Unit.Senate;
    const isDepartmentVisible =
        initialUserDetails.unit === Unit.Faculty ||
        initialUserDetails.unit === Unit.Sbc ||
        initialUserDetails.unit === Unit.Senate;

    // Create dynamic description based on visible fields
    const getDescriptionText = () => {
        if (isFacultyVisible && isDepartmentVisible) {
            return "Select faculty, department, and meeting to download prayer attachments";
        } else if (isDepartmentVisible) {
            return "Select department and meeting to download prayer attachments";
        } else {
            return "Select meeting to download prayer attachments";
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            title="Download Prayer Attachments"
            withCloseButton
            style={{ width: "500px" }}
        >
            <Box>
                <Text size="14px" color={theme.PrimaryGreyMid}>
                    {getDescriptionText()}
                </Text>
                <Spacer space={20} />

                {/* Faculty Selection - Only show for SBC and Senate units */}
                {(initialUserDetails.unit === Unit.Sbc ||
                    initialUserDetails.unit === Unit.Senate) && (
                        <Box>
                            <FilterLabelWrapper>
                                <IconWrapper>
                                    <Icon icon="layer" size={14} />
                                </IconWrapper>
                                <Text size="14px" color={theme.PrimaryGreyMid}>
                                    Faculty
                                </Text>
                            </FilterLabelWrapper>
                            <Select
                                placeholder="Select Faculty"
                                name="facultyId"
                                options={facultyOptions ?? []}
                                spaceTop
                                style={{ width: "100%" }}
                                isLoading={isLoadingFaculties}
                                onChange={handleFacultyChange}
                                value={selectedFacultyId}
                            />
                        </Box>
                    )}

                <Spacer space={16} />

                {/* Department Selection - Only show for Faculty, SBC, and Senate units */}
                {(initialUserDetails.unit === Unit.Faculty ||
                    initialUserDetails.unit === Unit.Sbc ||
                    initialUserDetails.unit === Unit.Senate) && (
                        <Box>
                            <FilterLabelWrapper>
                                <IconWrapper>
                                    <Icon icon="layer" size={14} />
                                </IconWrapper>
                                <Text size="14px" color={theme.PrimaryGreyMid}>
                                    Department
                                </Text>
                            </FilterLabelWrapper>
                            <Select
                                placeholder="Select Department"
                                name="departmentId"
                                options={filteredDepartmentOptions ?? []}
                                spaceTop
                                style={{ width: "100%" }}
                                isLoading={isLoadingDepartments}
                                onChange={handleDepartmentChange}
                                value={selectedDepartmentId}
                                disabled={!selectedFacultyId}
                            />
                        </Box>
                    )}

                <Spacer space={16} />

                {/* Meeting Selection */}
                <Box>
                    <FilterLabelWrapper>
                        <IconWrapper>
                            <Icon icon="judge" size={14} />
                        </IconWrapper>
                        <Text size="14px" color={theme.PrimaryGreyMid}>
                            Meeting
                        </Text>
                    </FilterLabelWrapper>
                    <Select
                        placeholder="Select Meeting"
                        name="meetingId"
                        options={meetingOptions}
                        spaceTop
                        style={{ width: "100%" }}
                        isLoading={isLoadingMeetings}
                        onChange={handleMeetingChange}
                        value={selectedMeetingId}
                    />
                </Box>

                <Spacer space={20} />

                {/* Action Buttons */}
                <Box
                    display="flex"
                    style={{
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: "12px",
                    }}
                >
                    <Button plain onClick={handleClose} style={{ flex: 1 }}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleDownloadAttachments}
                        disabled={
                            isDownloadingAttachmentsByMeetingAndDepartment ||
                            !selectedMeetingId ||
                            ((initialUserDetails.unit === Unit.Faculty ||
                                initialUserDetails.unit === Unit.Sbc ||
                                initialUserDetails.unit === Unit.Senate) &&
                                !selectedDepartmentId)
                        }
                        progress={isDownloadingAttachmentsByMeetingAndDepartment}
                        style={{ flex: 1 }}
                    >
                        {isDownloadingAttachmentsByMeetingAndDepartment
                            ? "Downloading..."
                            : "Download Attachments"}
                    </Button>
                </Box>
            </Box>
        </Modal>
    );
};
